import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";

import { useT } from "../../i18n";

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * A centered modal dialog with an overlay. Closes on Esc or backdrop click,
 * focuses itself on open, and traps Tab within its focusable elements
 * (brief §13 accessibility).
 */
export function Modal({ title, onClose, children }: ModalProps) {
  const t = useT();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    card?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !card) return;
      const focusable = card.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="w-full max-w-sm overflow-hidden rounded-panel border border-border-subtle bg-surface-overlay shadow-lg outline-none"
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-4 py-2.5">
          <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
          <button
            type="button"
            aria-label={t.export.close}
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-text-tertiary transition-colors hover:bg-surface-sunken hover:text-text-primary"
          >
            <X size={16} />
          </button>
        </header>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
